import {Component, OnDestroy} from '@angular/core';
import {CurrencyPipe} from '@angular/common';
import {BehaviorSubject, Observable, of, shareReplay, Subject, takeUntil} from 'rxjs';
import {catchError, finalize, map, switchMap, tap} from 'rxjs/operators';
import {MenuItem} from 'primeng/api';
import {
    EcommerceDashboardPeriod,
    RefundDashboardStats,
    RefundSearchResultPage,
    RefundSearchResultView,
    WebRefundDashboardService,
    WebRefundService
} from 'web-angular-client';
import {ROUTES} from '../../../app.routes';
import {AlertService} from '../../../sdk/service/alert.service';
import {DropdownItem} from '../../../sdk/interface/data/DropdownItem';
import {ECOMMERCE_PERIOD_OPTIONS} from '../ecommerce-period-options';
import {RefundRowActionsService} from '../../refund/refund-row-actions.service';
import {browserTimezone} from '../../../sdk/component/search/ecommerce-account-search.service';

/**
 * Refund dashboard page: a period selector bar over three summary cards — Cancelled & Failed, Refunds Issued, and
 * Refund Rate — plus a recent-refunds preview. The cards are driven by the web refund-dashboard stats endpoint
 * (backend returns raw counts; this component derives the ratios/averages); the recent-refunds card reuses the shared
 * refund table fed by the existing refund search (a few rows, not period-filtered) — mirroring how the ecommerce
 * dashboard's recent-transactions card reuses account search.
 *
 * <p>
 * The table runs in {@code clientSideSort} mode: sorting reorders the loaded rows and never re-queries, so the card
 * stays the most recent few rather than becoming "the largest refunds ever". Sorting across every refund is the
 * refund transactions page's job.
 */
@Component({
    selector: 'app-refund-dashboard',
    templateUrl: './refund-dashboard.component.html',
    styleUrls: ['./refund-dashboard.component.scss'],
    standalone: false,
    providers: [CurrencyPipe]
})
export class RefundDashboardComponent implements OnDestroy
{
    private static readonly RECENT_LIMIT = 5;

    /** Seeds the table's sort indicator to match the order the card is actually loaded in. */
    readonly defaultSortField: string = 'dateTimeCreated';

    readonly defaultSortOrder = 'DESC' as const;

    readonly periodOptions: DropdownItem[] = ECOMMERCE_PERIOD_OPTIONS;

    readonly refundTransactionsPath = ROUTES.SECURE.ACCOUNT.REFUND_TRANSACTIONS.path;

    private readonly destroy$ = new Subject<void>();

    readonly selectedPeriod$ = new BehaviorSubject<EcommerceDashboardPeriod>(EcommerceDashboardPeriod.LAST_7_D);

    readonly statsLoading$ = new BehaviorSubject<boolean>(false);

    readonly stats$: Observable<RefundDashboardStats | null> = this.selectedPeriod$.pipe(
        tap(() => this.statsLoading$.next(true)),
        switchMap(period =>
            this.webRefundDashboardService.getRefundDashboardStats(period, browserTimezone()).pipe(
                finalize(() => this.statsLoading$.next(false)),
                catchError((err) => this.handleLoadError(
                    err,
                    undefined,
                    'Failed to load refund dashboard stats.',
                    null))
            )
        ),
        takeUntil(this.destroy$),
        shareReplay(1)
    );

    readonly cancelledFailedTotal$ = this.stats$.pipe(
        map(stats => stats ? stats.cancelledRefundCount + stats.failedRefundCount : 0)
    );

    readonly refundRatePercent$ = this.stats$.pipe(
        map(stats => stats && stats.paidTransactionCount > 0
            ? (stats.refundedTransactionCount / stats.paidTransactionCount) * 100
            : 0)
    );

    readonly cancelledFailedDescription$ = this.stats$.pipe(
        map(stats => stats ? `${stats.cancelledRefundCount} cancelled, ${stats.failedRefundCount} failed` : '')
    );

    readonly issuedDescription$ = this.stats$.pipe(
        map(stats =>
        {
            if (!stats)
            {
                return '';
            }
            const avgMajor = (stats.successfulRefundCount ? stats.successfulRefundTotalAmount / stats.successfulRefundCount : 0) / 100;
            return `${stats.successfulRefundCount} refunds · Average ${this.currencyPipe.transform(avgMajor, 'R ')}`;
        })
    );

    readonly refundRateDescription$ = this.stats$.pipe(
        map(stats => stats ? `${stats.refundedTransactionCount} of ${stats.paidTransactionCount} transactions` : '')
    );

    readonly recentLoading$ = new BehaviorSubject<boolean>(false);
    readonly recentError$ = new BehaviorSubject<boolean>(false);

    private readonly refreshRecent$ = new BehaviorSubject<void>(undefined);

    /**
     * The card's row set: always the five most recent. The table sorts these rows itself, so sorting reorders the five
     * rather than changing which five they are.
     */
    readonly recentRefunds$: Observable<RefundSearchResultView[]> = this.refreshRecent$.pipe(
        tap(() =>
        {
            this.recentLoading$.next(true);
            this.recentError$.next(false);
        }),
        switchMap(() =>
            this.webRefundService
                .searchRefunds(
                    0, RefundDashboardComponent.RECENT_LIMIT, this.defaultSortField, this.defaultSortOrder,
                    undefined, browserTimezone(), {})
                .pipe(
                    finalize(() => this.recentLoading$.next(false)),
                    map((page: RefundSearchResultPage) => page?.content ?? []),
                    catchError((err) =>
                        this.handleLoadError(err, this.recentError$, 'Failed to load recent refunds.', []))
                )),
        takeUntil(this.destroy$),
        shareReplay(1)
    );

    readonly buildMenuItemsForRefund = (row: RefundSearchResultView): MenuItem[] =>
        this.refundRowActions.buildMenuItems(row, () => this.refreshRecent$.next());

    constructor(
        private readonly webRefundDashboardService: WebRefundDashboardService,
        private readonly webRefundService: WebRefundService,
        private readonly refundRowActions: RefundRowActionsService,
        private readonly alertService: AlertService,
        private readonly currencyPipe: CurrencyPipe)
    {
    }

    ngOnDestroy(): void
    {
        this.destroy$.next();
        this.destroy$.complete();
    }

    onPeriodChange(period: EcommerceDashboardPeriod): void
    {
        if (period)
        {
            this.selectedPeriod$.next(period);
        }
    }

    /**
     * Marks the supplied error subject (so a card can show its placeholder) and surfaces a toast for non-503 failures,
     * then degrades to a fallback rather than crashing. A 503 ("Service unavailable") gets only the inline placeholder
     * — no toast — mirroring {@code WebEcommerceDashboardService}'s handling.
     */
    private handleLoadError<T>(
        err: any,
        errorSubject: BehaviorSubject<boolean> | undefined,
        toastMessage: string,
        fallback: T): Observable<T>
    {
        errorSubject?.next(true);
        if (err?.status !== 503)
        {
            this.alertService.error(toastMessage);
        }
        return of(fallback);
    }
}
